import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { loadArticles, loadArticle, savedAgo } from "./cache";
import {
  appConfig,
  submitFeedback,
  supportAvailable,
  validaideReaderFor,
  type ArticleFull,
  type ArticleListItem,
  type WidgetConfig,
} from "./data";
import { buildCopyLink, buildStandaloneUrl, parseDeepLink } from "./deeplink";
import SupportForm, { discardDraft, type SupportDraft } from "./SupportForm";
import { clamp, isDragHandle, pastDragThreshold, type Position } from "./drag";
import { askQuestion, type AskSource } from "./ask";
import { readingMinutes, renderMarkdown } from "./markdown";
import { relevantArticles } from "./relevance";
import { orderFolderItems } from "./ordering";
import { groupByCategory } from "@skycell-ag/kb-core";
import { isZoomable, viewport } from "./zoom";
import { readVote, writeVote } from "./vote";
import { AUTH_EVENT, resolveAuthToken } from "./viewer";
import { ArticleRow, EMPTY_ITEM, EmptyResults, RELEVANT_EMPTY } from "./rows";
import {
  BackIcon,
  BookIcon,
  CheckIcon,
  ChevronRight,
  ChevronUpIcon,
  CloseIcon,
  CompressIcon,
  ExpandIcon,
  ExternalLinkIcon,
  FolderIcon,
  LinkIcon,
  MessageIcon,
  MinusIcon,
  SearchIcon,
  SendIcon,
  SparkIcon,
  ThumbDownIcon,
  ThumbUpIcon,
} from "./icons";

// Tallest the growing search box gets before it scrolls internally (~5 lines at
// the 20px line-height set on .kb-search-field textarea).
const MAX_SEARCH_HEIGHT = 100;

/**
 * One submitted question and what has come back for it.
 *
 * `thinking` covers the wait before the first token; `text` accumulates the
 * streamed answer; `answered` flips on the terminal event. An answered state
 * with empty `text` IS the no-answer case — the panel then offers `sources`
 * (what retrieval found) instead, which is exactly the design's grey panel.
 */
type AskState = {
  question: string;
  thinking: boolean;
  text: string;
  sources: AskSource[];
  answered: boolean;
  failed: boolean;
};

export default function App({ config }: { config: WidgetConfig }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Collapsed to its header bar, the way a chat window minimizes: the panel
  // stays on screen and keeps every bit of state (open article, question,
  // answer, scroll), so "_" is a way to get the panel out of the way for a
  // moment rather than a second close button. Closing is still "×".
  const [collapsed, setCollapsed] = useState(false);
  // The contact form (footer → "Contact support"). A view, not a dialog, so it
  // shares the panel chrome and the header's back chevron unwinds out of it.
  const [contact, setContact] = useState(false);
  // What the reader has typed into it so far. A ref, not state: the form view
  // is unmounted by Esc / back / Collapse and must come back with its fields
  // and screenshots intact (the header docs promise exactly that). Cleared by
  // the form on Cancel / send, and here on Close, which resets the panel.
  const supportDraft = useRef<SupportDraft | null>(null);
  // Can the server actually deliver a support request (does it have SMTP)?
  // null = not asked yet. Probed once per panel-open so the form is never
  // offered when submitting it could only fail.
  const [supportOk, setSupportOk] = useState<boolean | null>(null);
  const [tab, setTab] = useState<"relevant" | "all">(config.defaultTab);
  const [loaded, setLoaded] = useState(false);
  const [items, setItems] = useState<ArticleListItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  // When the network fails but the browser still holds a copy, the panel keeps
  // working and says so. savedAt is when that copy was last fetched.
  const [staleSince, setStaleSince] = useState<number | null>(null);
  // Bumped by the list's "Try again" button to re-run the load effect: the
  // transient 503s worth retrying (a deploy's schema-cache reload) outlive
  // fetchWithRetry's own few hundred ms of backoff.
  const [reloadKey, setReloadKey] = useState(0);
  /**
   * Where "Open in the Knowledge Base" and Copy link point.
   *
   * Starts at the derived default — the host serving this bundle — and is
   * replaced by the Validaide reader host when the server reports one, because
   * for a Validaide reader the default is not merely suboptimal: it is the
   * SkyCell reader, which asks for a Keycloak account they do not have. An
   * explicit data-standalone-url always wins, the server answers null for
   * every deployment that has no Validaide host, and only the `validaide` app
   * swaps at all — every other app's readers have SkyCell accounts and stay on
   * the default host.
   */
  const [standaloneBase, setStandaloneBase] = useState(config.standaloneUrl);
  useEffect(() => {
    if (config.standaloneUrlExplicit || !validaideReaderFor(config.app)) return;
    let active = true;
    void appConfig(config).then((c) => {
      if (active && c.validaideReaderBase) setStandaloneBase(c.validaideReaderBase);
    });
    return () => {
      active = false;
    };
  }, [config]);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<ArticleFull | null>(null);
  const [articleError, setArticleError] = useState<string | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  // Which folder the reader has opened in the All tab, or null at the tile
  // grid. Folders behave like folders: you go into one and come back out
  // (VALCM-7101) rather than expanding every accordion at once.
  const [openFolder, setFolder] = useState<string | null>(null);
  // "Was this article helpful?" — the vote for the open article, or null while
  // unanswered. Seeded from localStorage so a returning reader isn't re-asked.
  const [vote, setVote] = useState<boolean | null>(null);
  // The AI answer for the question that was submitted (arrow / Enter), or null
  // when the reader is just filtering the list. See askState below.
  const [ask, setAsk] = useState<AskState | null>(null);
  const askAbort = useRef<AbortController | null>(null);
  // AI answers need both an endpoint (derived from this script's origin) and a
  // share token (retrieval is token-scoped). Without them the widget is
  // search-only and the send arrow stays disabled.
  const canAsk = Boolean(config.askUrl && config.token);
  // Relevance keys: data-context is the initial value; SPA hosts re-point them
  // per route via SkyCellKB.setContext(). Optional — see `href` below.
  const [context, setContext] = useState<string[]>(config.context);
  // The host page URL, which is what makes the Relevant tab work when nobody
  // has declared any context (see relevance.ts). Re-read whenever the panel is
  // opened — the reader navigates first and asks for help second — and on the
  // navigation events a host router fires.
  const [href, setHref] = useState(() => window.location.href);
  // Full-screen preview of an article image (lightbox). null = closed.
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  // "Copied" feedback on the article Share button. Reset on article change.
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // Each #skycell-kb=<slug> hash value auto-opens exactly once — closing the
  // widget sticks until the hash changes to a NEW value.
  const consumedHashSlug = useRef<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- moving the panel ------------------------------------------------------
  // Readers drag the panel because it is covering the very thing they are
  // reading about. null = docked by CSS to the data-side corner; once dragged
  // it switches to explicit viewport coordinates (see drag.ts).
  const cardRef = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<Position | null>(null);
  // Live drag state in a REF, not state: pointerdown/move/up can all arrive
  // before a re-render flushes, and a handler reading a stale `false` would drop
  // the first move — worse, a stale `false` in the pointerup handler would leave
  // the drag "active" forever, so a quick click on the header made the panel
  // follow the cursor with no button held. `active` is the authority; `dragging`
  // below exists only to put the class on the card.
  //
  // dx/dy are the grab point within the card, so the panel tracks the cursor
  // from wherever it was picked up instead of snapping its corner under it.
  // x/y are where the press started: it only becomes a drag once the pointer
  // has travelled DRAG_THRESHOLD from there, and `moved` records whether it
  // did, for the header's click handler that runs right after the release.
  const drag = useRef<{
    active: boolean;
    moved: boolean;
    x: number;
    y: number;
    dx: number;
    dy: number;
  }>({ active: false, moved: false, x: 0, y: 0, dx: 0, dy: 0 });
  const [dragging, setDragging] = useState(false);

  /** Re-clamp the current position against the card's CURRENT size. */
  const reclamp = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    setPos((p) =>
      p ? clamp(p, { width: rect.width, height: rect.height }, viewport()) : p,
    );
  }, []);

  const onHeaderPointerDown = useCallback((e: PointerEvent) => {
    const card = cardRef.current;
    if (!card || !isDragHandle(e.target, e.button)) return;
    const rect = card.getBoundingClientRect();
    // Nothing moves yet: until the pointer travels this is a click, and taking
    // the panel off its CSS dock on a click would strand a collapsed bar
    // wherever it was clicked (SAT-1088). The first real move does that.
    drag.current = {
      active: true,
      moved: false,
      x: e.clientX,
      y: e.clientY,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
    };
    // Pointer capture keeps the drag alive over the host page's own elements
    // (and iframes), and past the edge of the header.
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    e.preventDefault(); // no text selection while dragging
  }, []);

  const onHeaderPointerMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d.active) return;
    if (!d.moved) {
      if (!pastDragThreshold({ x: d.x, y: d.y }, { x: e.clientX, y: e.clientY })) return;
      d.moved = true;
      setDragging(true);
    }
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    setPos(
      clamp(
        { left: e.clientX - d.dx, top: e.clientY - d.dy },
        { width: rect.width, height: rect.height },
        viewport(),
      ),
    );
  }, []);

  const endDrag = useCallback((e: PointerEvent) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }, []);

  // A window that shrinks — or a panel that grows, by expanding or by being
  // restored from collapsed — must not leave the panel stranded off-screen.
  // Depend on WHETHER the panel is positioned, not on the coordinates: the
  // effects below would otherwise re-run on every pointermove.
  const positioned = pos !== null;

  useEffect(() => {
    if (!positioned || !open) return;
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [positioned, open, reclamp]);

  // A panel that grows — expanded, or restored from collapsed — can end up
  // hanging off the viewport. Its width animates (.kb-card's transition), so
  // re-clamping straight after the render measured the old, narrow card and let
  // an expanded panel restored from a dragged bar grow past the right edge,
  // Close button and all (SAT-1091). A ResizeObserver re-clamps on every frame
  // of the transition and once it settles, so the size it clamps is never stale.
  useEffect(() => {
    const card = cardRef.current;
    if (!positioned || !open || !card) return;
    const observer = new ResizeObserver(reclamp);
    observer.observe(card);
    return () => observer.disconnect();
  }, [open, positioned, reclamp]);

  // Keep the host URL current. `popstate`/`hashchange` cover ordinary
  // navigation; re-reading on open covers SPA routers that push state without
  // firing either. We deliberately don't patch the host's history API to catch
  // every route change — reaching into someone else's globals for a slightly
  // fresher relevance list is a bad trade.
  useEffect(() => {
    const sync = () => setHref(window.location.href);
    if (open) sync();
    window.addEventListener("popstate", sync);
    window.addEventListener("hashchange", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("hashchange", sync);
    };
  }, [open]);

  // Load the article list lazily, the first time the panel is opened.
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    setListError(null);
    loadArticles(config)
      .then(({ data, stale, savedAt }) => {
        if (!cancelled) {
          setItems(data);
          setLoaded(true);
          setStaleSince(stale ? savedAt : null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setListError(e instanceof Error ? e.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, [open, loaded, config, reloadKey]);

  // Is the contact form usable? One cheap GET the first time the panel opens.
  // Requires a token as well as the endpoint — the share token is what
  // authorizes the request, exactly as it does for AI answers.
  useEffect(() => {
    if (!open || supportOk !== null || !config.supportUrl || !config.token) return;
    let cancelled = false;
    void supportAvailable(config).then((ok) => {
      if (!cancelled) setSupportOk(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [open, supportOk, config]);

  const openArticle = useCallback(
    (slug: string) => {
      if (errorTimer.current) clearTimeout(errorTimer.current);
      setArticleError(null);
      setLoadingArticle(true);
      // Friendly + transient: the deep-link/not-found error explains itself and
      // auto-clears rather than sticking to the list forever.
      const fail = (msg: string) => {
        setArticleError(msg);
        errorTimer.current = setTimeout(() => setArticleError(null), 5000);
      };
      return loadArticle(config, slug)
        .then(({ data: a, stale, savedAt }) => {
          if (a) {
            setActive(a);
            // A cached article implies the network is down; keep the notice up
            // rather than clearing it just because this read "succeeded".
            if (stale) setStaleSince(savedAt);
            return true;
          }
          fail("That article isn't available here.");
          return false;
        })
        .catch((e: unknown) => {
          fail(e instanceof Error ? e.message : "Failed to load article");
          return false;
        })
        .finally(() => setLoadingArticle(false));
    },
    [config],
  );

  // Clear a pending error timeout on unmount.
  useEffect(
    () => () => {
      if (errorTimer.current) clearTimeout(errorTimer.current);
    },
    [],
  );

  // The reader changed (the host called setAuthToken with a different user, or
  // signed them out). Everything on screen was filtered for the previous
  // reader's entitlements, so drop it and load again rather than show one
  // person's articles to another.
  useEffect(() => {
    const onAuth = () => {
      setActive(null);
      setItems([]);
      setLoaded(false);
      setStaleSince(null);
      setReloadKey((n) => n + 1);
    };
    window.addEventListener(AUTH_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_EVENT, onAuth);
  }, []);

  // Every opening starts on the app's default tab (Relevant, except where
  // ALL_BY_DEFAULT says otherwise), not the one the reader last left it on. Keyed
  // on `open` rather than done in closeAll, because a host closing through
  // SkyCellKB.toggle()/close() never goes through closeAll.
  useEffect(() => {
    if (!open) setTab(config.defaultTab);
  }, [open, config.defaultTab]);

  // Ask the host who is reading when the panel opens, and again when the tab is
  // shown after being hidden. The reads resolve a token of their own, so this is
  // only about NOTICING a change — someone signing out in another tab — while
  // the panel sits open, instead of at whatever read happens next. Resolving
  // fires AUTH_EVENT above if the reader really did change; a host with no
  // provider registered is a no-op.
  useEffect(() => {
    if (!open) return;
    void resolveAuthToken();
    const onVisible = () => {
      if (!document.hidden) void resolveAuthToken();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [open]);

  // Programmatic control (window.SkyCellKB in index.ts): hosts that hide the
  // launcher open/close the panel through these window CustomEvents. open()
  // optionally carries { slug } in detail to jump straight to an article.
  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true);
      // A host asking for the widget wants to SEE it: a panel left collapsed
      // from an earlier visit must not swallow the call.
      setCollapsed(false);
      const slug = (e as CustomEvent<{ slug?: string }>).detail?.slug;
      if (slug) openArticle(slug);
    };
    const onClose = () => setOpen(false);
    const onToggle = () => setOpen((v) => !v);
    const onContext = (e: Event) =>
      setContext((e as CustomEvent<{ context?: string[] }>).detail?.context ?? []);
    window.addEventListener("skycell-kb:open", onOpen);
    window.addEventListener("skycell-kb:close", onClose);
    window.addEventListener("skycell-kb:toggle", onToggle);
    window.addEventListener("skycell-kb:context", onContext);
    return () => {
      window.removeEventListener("skycell-kb:open", onOpen);
      window.removeEventListener("skycell-kb:close", onClose);
      window.removeEventListener("skycell-kb:toggle", onToggle);
      window.removeEventListener("skycell-kb:context", onContext);
    };
  }, [openArticle]);

  // Deep link: #skycell-kb=<slug> in the host URL opens the widget on that
  // article — on load and on later hash changes. Each hash value is consumed
  // once (closing the widget sticks; a NEW slug re-opens). The widget only ever
  // reads location.hash — it never writes it (host SPA routers stay untouched).
  useEffect(() => {
    const applyHash = () => {
      const slug = parseDeepLink(window.location.hash);
      if (!slug || slug === consumedHashSlug.current) return;
      consumedHashSlug.current = slug;
      setOpen(true);
      setCollapsed(false);
      void openArticle(slug).then((ok) => {
        // A failed fetch must not burn the deep link for this page load — free
        // the slug so a retry (hash toggle / SkyCellKB.open) can work. Success
        // keeps it consumed: closing the widget then sticks, as intended.
        if (!ok && consumedHashSlug.current === slug)
          consumedHashSlug.current = null;
      });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [openArticle]);

  // Escape unwinds one layer at a time: the lightbox, then the contact form,
  // then the panel. Leaving the form this way keeps what was typed in it — only
  // Cancel / a successful send discards it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (lightboxSrc) setLightboxSrc(null);
      else if (contact) setContact(false);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, lightboxSrc, contact]);

  // Copy a link to the active article in the standalone KB app, falling back to
  // the host-page deep link (see buildCopyLink).
  // navigator.clipboard needs a secure context; fall back to execCommand.
  const copyShareLink = useCallback((slug: string) => {
    const url = buildCopyLink(standaloneBase, window.location.href, slug);
    const done = () => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    };
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        if (document.execCommand("copy")) done();
      } finally {
        ta.remove();
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(fallback);
    } else {
      fallback();
    }
  }, [standaloneBase]);

  // Reset "Copied" feedback when the article changes / on unmount.
  useEffect(() => {
    setCopied(false);
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, [active]);

  // Restore any previous vote for the newly-opened article, so a reader who
  // already answered sees the thank-you instead of being asked again.
  useEffect(() => {
    setVote(active ? readVote(active.id) : null);
  }, [active]);

  const castVote = useCallback(
    (helpful: boolean) => {
      if (!active) return;
      setVote(helpful);
      writeVote(active.id, helpful);
      // Best-effort: the reader has been thanked either way, so a failed
      // insert must not turn into an error message.
      void submitFeedback(config, active.id, helpful).catch(() => {});
    },
    [active, config],
  );

  // Handle clicks inside rendered markdown:
  //  • an <img> opens full-screen in the lightbox;
  //  • an internal /kb/<slug> link opens that article in-widget
  //    (external links keep their new-tab behaviour).
  const onContentClick = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const img = target?.closest?.("img");
      if (img instanceof HTMLImageElement) {
        if (!isZoomable(img)) return; // small inline images (buttons/icons) aren't zoomable
        e.preventDefault();
        setLightboxSrc(img.currentSrc || img.src);
        return;
      }
      const anchor = target?.closest?.("a");
      const href = anchor?.getAttribute("href") ?? "";
      const slug = href.match(/\/kb\/([^/?#]+)/)?.[1];
      if (slug) {
        e.preventDefault();
        openArticle(slug);
      }
    },
    [openArticle],
  );

  // Category, then section, as the Zendesk Help Center reads — one tile per
  // full path turned every one-article section into its own folder.
  const grouped = useMemo(() => groupByCategory(items, orderFolderItems), [items]);
  // Search and the Relevant tab read the same display order as the folders
  // (newest-first inside versioned folders), not the raw server order.
  const ordered = useMemo(
    () => grouped.flatMap((g) => g.sections.flatMap(([, list]) => list)),
    [grouped],
  );

  const searching = query.trim().length > 0;
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return ordered.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.folder.toLowerCase().includes(q) ||
        a.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [ordered, query]);

  // Articles to list under an AI panel: what retrieval found, as full rows
  // (folder indicator included) when the list knows them; otherwise the
  // client-side title matches, so a no-answer never strands the reader.
  const askArticles = useMemo<ArticleListItem[]>(() => {
    if (!ask) return [];
    if (ask.sources.length > 0) {
      return ask.sources.map(
        (s) => ordered.find((a) => a.slug === s.slug) ?? { ...EMPTY_ITEM, slug: s.slug, title: s.title },
      );
    }
    return searchResults;
  }, [ask, ordered, searchResults]);

  const relevant = useMemo(
    () => relevantArticles(ordered, context, href),
    [ordered, context, href],
  );
  const folderSections = useMemo(
    () => grouped.find((g) => g.category === openFolder)?.sections ?? [],
    [grouped, openFolder],
  );
  const articleHtml = useMemo(
    () => (active ? renderMarkdown(active.content, config.kbUrl) : ""),
    [active, config.kbUrl],
  );
  // Footer link out to the standalone app. Article-scoped: there is no
  // "current article" to open from the list, and a bare app link would just be
  // a second, worse copy of the panel the reader is already in.
  const standaloneHref = useMemo(
    () => (active ? buildStandaloneUrl(standaloneBase, active.slug) : null),
    [active, standaloneBase],
  );

  // Tag content images as `.kb-zoomable` once their natural size is known, so
  // only big images show the zoom-in cursor. Images may not be loaded when the
  // HTML mounts, so decide on `load` for any that aren't complete yet. The click
  // handler applies the same `isZoomable` gate.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const mark = (img: HTMLImageElement) => img.classList.toggle("kb-zoomable", isZoomable(img));
    const cleanups: Array<() => void> = [];
    for (const img of Array.from(root.querySelectorAll("img"))) {
      // A forcing class (kb-zoom / kb-no-zoom) is decidable without the natural
      // size; otherwise wait for load so the area check has real dimensions.
      const forced = img.classList.contains("kb-zoom") || img.classList.contains("kb-no-zoom");
      if (forced || (img.complete && img.naturalWidth)) {
        mark(img);
      } else {
        const onLoad = () => mark(img);
        img.addEventListener("load", onLoad);
        cleanups.push(() => img.removeEventListener("load", onLoad));
      }
    }
    return () => cleanups.forEach((fn) => fn());
  }, [articleHtml]);

  // Submitting the question (arrow button / Enter) asks the KB for an answer.
  // Typing keeps filtering the loaded list underneath, so the reader always has
  // the article results whether or not an answer comes back.
  const submitQuestion = useCallback(() => {
    const q = query.trim();
    if (q.length < 3 || !canAsk) return;
    askAbort.current?.abort();
    const controller = new AbortController();
    askAbort.current = controller;
    setAsk({ question: q, thinking: true, text: "", sources: [], answered: false, failed: false });
    void askQuestion(
      config,
      q,
      (e) => {
        if (controller.signal.aborted) return;
        setAsk((prev) => {
          if (!prev || prev.question !== q) return prev; // a newer question won
          switch (e.type) {
            case "sources":
              return { ...prev, sources: e.sources };
            case "delta":
              return { ...prev, thinking: false, text: prev.text + e.text };
            case "done":
              // Ungrounded: whatever streamed was a hedge that ended in the
              // sentinel, not an answer — fall back to the no-answer panel.
              return { ...prev, thinking: false, answered: true, text: e.grounded ? prev.text : "" };
            case "error":
              // Degrade to the client-side list rather than showing plumbing.
              return { ...prev, thinking: false, answered: true, failed: true };
          }
        });
      },
      controller.signal,
    );
  }, [query, canAsk, config]);

  // Editing the question invalidates the answer that was on screen for the old
  // one; abandoning the search clears it entirely.
  useEffect(() => {
    setAsk((prev) => (prev && prev.question !== query.trim() ? null : prev));
  }, [query]);

  useEffect(() => () => askAbort.current?.abort(), []);

  // The search box grows with its content (1 → MAX_SEARCH_ROWS lines) instead of
  // scrolling a long question sideways in a single-line input.
  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_SEARCH_HEIGHT)}px`;
  };

  // An answer (or the wait for one) is on screen. A failed ask shows nothing —
  // the widget degrades to plain search.
  const answerShown = Boolean(ask && !ask.failed);

  const closeAll = () => {
    setOpen(false);
    setActive(null);
    setArticleError(null);
    setQuery("");
    setFolder(null);
    setLightboxSrc(null);
    setContact(false);
    discardDraft(supportDraft);
    // Closing is the deliberate "I'm done" gesture, so the panel starts docked
    // and full-size next time rather than collapsed wherever it was dragged.
    setCollapsed(false);
    setPos(null);
  };

  const listBody = (
    <>
      {!searching && (
        <div class="kb-tabs" role="tablist">
          <button
            class={`kb-tab${tab === "relevant" ? " active" : ""}`}
            role="tab"
            aria-selected={tab === "relevant"}
            onClick={() => setTab("relevant")}
          >
            Relevant Articles
          </button>
          <button
            class={`kb-tab${tab === "all" ? " active" : ""}`}
            role="tab"
            aria-selected={tab === "all"}
            onClick={() => setTab("all")}
          >
            All Articles
          </button>
        </div>
      )}

      <div class="kb-search">
        <div class="kb-search-field">
          <span class="kb-search-icon"><SearchIcon /></span>
          <textarea
            rows={1}
            placeholder="Ask anything…"
            aria-label="Search articles"
            value={query}
            onInput={(e) => {
              const el = e.target as HTMLTextAreaElement;
              setQuery(el.value);
              autoGrow(el);
            }}
            // Enter asks; Shift+Enter is a newline in a multi-line question.
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitQuestion();
              }
            }}
            ref={(el) => {
              if (el) autoGrow(el);
            }}
          />
          {query && (
            <button class="kb-search-clear" aria-label="Clear" onClick={() => setQuery("")}>
              <CloseIcon />
            </button>
          )}
        </div>
        <button
          class="kb-send"
          aria-label="Ask"
          title={canAsk ? "Ask" : undefined}
          disabled={!canAsk || query.trim().length < 3}
          onClick={submitQuestion}
        >
          <SendIcon />
        </button>
      </div>

      <div class="kb-body">
        {loadingArticle && <div class="kb-state">Loading…</div>}
        {staleSince !== null && (
          <div class="kb-stale" role="status">
            Showing a saved copy from {savedAgo(staleSince)} — the knowledge base
            can't be reached right now.
          </div>
        )}
        {articleError && <div class="kb-state kb-error">{articleError}</div>}
        {listError && (
          <div class="kb-state kb-error">
            {listError}
            <button
              type="button"
              class="kb-retry"
              onClick={() => {
                setListError(null);
                setReloadKey((n) => n + 1);
              }}
            >
              Try again
            </button>
          </div>
        )}
        {!listError && !loaded && <div class="kb-state">Loading…</div>}

        {/* AI panels take the body while a question is live: the thinking
            screen alone, then the answer bubble (or the grey no-answer line)
            with the relevant articles as rows underneath. A failed ask renders
            nothing: search alone, as if never asked. */}
        {answerShown && ask && ask.thinking && (
          <div class="kb-thinking" role="status" aria-live="polite">
            <span class="kb-thinking-text">
              <span class="kb-dots"><i /><i /><i /></span>
              Thinking…
            </span>
          </div>
        )}
        {answerShown && ask && !ask.thinking && (
          askArticles.length === 0 && !ask.text ? (
            // Nothing to answer with and nothing to point at: the empty state,
            // not a promise of articles that never appear.
            <EmptyResults centre />
          ) : (
            <div>
              {ask.text ? (
                <div class="kb-answer">
                  <div class="kb-answer-label"><SparkIcon />AI Answer</div>
                  <div class="kb-answer-text">{ask.text}</div>
                </div>
              ) : (
                <div class="kb-answer none">
                  We found a few articles that may answer your question.
                </div>
              )}
              {askArticles.length > 0 && (
                <div class="kb-list-stack">
                  {askArticles.map((a) => (
                    <ArticleRow key={a.slug} article={a} showFolder onOpen={openArticle} />
                  ))}
                </div>
              )}
            </div>
          )
        )}

        {/* Plain title search, while no question is live. */}
        {loaded && searching && !answerShown && (
          <div class="kb-results">
            <div class="kb-status-line">
              {searchResults.length === 0
                ? "No results in all articles"
                : `${searchResults.length} result${searchResults.length === 1 ? "" : "s"} in all articles`}
            </div>
            {searchResults.length > 0 ? (
              <div class="kb-list-stack">
                {searchResults.map((a) => (
                  <ArticleRow key={a.id} article={a} showFolder onOpen={openArticle} />
                ))}
              </div>
            ) : tab === "relevant" ? (
              <EmptyResults centre {...RELEVANT_EMPTY} />
            ) : (
              <EmptyResults centre />
            )}
          </div>
        )}

        {loaded && !searching && tab === "relevant" && (
          relevant.length === 0 ? (
            <EmptyResults centre {...RELEVANT_EMPTY} />
          ) : (
            <div class="kb-list-stack">
              {relevant.map((a) => (
                <ArticleRow key={a.id} article={a} showFolder onOpen={openArticle} />
              ))}
            </div>
          )
        )}

        {loaded && !searching && tab === "all" && (
          ordered.length === 0 ? (
            <div class="kb-state">No articles yet.</div>
          ) : config.folders === "none" ? (
            // Folder level switched off (data-folders="none"): one flat list.
            <div class="kb-list-stack">
              {ordered.map((a) => (
                <ArticleRow key={a.id} article={a} showFolder onOpen={openArticle} />
              ))}
            </div>
          ) : openFolder ? (
            // Inside a folder. The header's back chevron comes back out.
            <div>
              <div class="kb-status-line">{openFolder}</div>
              {folderSections.map(([section, list]) => (
                <div key={section ?? ""} class="kb-section">
                  {section && <div class="kb-section-title">{section}</div>}
                  <div class="kb-list-stack">
                    {list.map((a) => (
                      <ArticleRow key={a.id} article={a} onOpen={openArticle} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Folder tiles. Expanded, they lay out as an even grid — one
            // same-sized cell per folder; narrow, the same grid falls back to a
            // single column of rows.
            <div class={`kb-folder-grid${expanded ? " tiles" : ""}`}>
              {grouped.map(({ category, count }) => (
                <button
                  key={category}
                  class="kb-folder-tile"
                  onClick={() => setFolder(category)}
                >
                  <FolderIcon class="kb-folder-glyph" />
                  <span class="kb-folder-name">{category}</span>
                  <span class="kb-folder-meta">
                    {count} article{count === 1 ? "" : "s"}
                  </span>
                  <span class="kb-folder-chevron"><ChevronRight /></span>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </>
  );

  // The back chevron is always in the header (per design). It unwinds one level
  // at a time — contact form → article → search → open folder — and is disabled
  // at the root.
  const canGoBack = contact || Boolean(active) || searching || Boolean(openFolder);
  const goBack = () => {
    if (contact) setContact(false);
    else if (active) setActive(null);
    else if (searching) setQuery("");
    else setFolder(null);
  };

  const side = ` side-${config.side}`;

  return (
    <div>
      {!open && config.launcher && (
        <button
          class={`kb-launcher${side}`}
          onClick={() => {
            setOpen(true);
            setCollapsed(false);
          }}
          aria-haspopup="dialog"
        >
          <BookIcon />
          {config.launcherLabel}
        </button>
      )}

      {open && (
        <section
          ref={cardRef}
          class={
            `kb-card${side}` +
            `${expanded && !collapsed ? " expanded" : ""}` +
            `${collapsed ? " collapsed" : ""}` +
            `${pos ? " floating" : ""}` +
            `${dragging ? " dragging" : ""}`
          }
          // Expanded width is host-configurable (data-expanded-width); the CSS
          // max-width still keeps it inside the viewport. Once dragged, explicit
          // coordinates replace the CSS corner dock (see drag.ts) — `right`/
          // `bottom` are cleared by .kb-card.floating rather than inline.
          style={{
            "--kb-expanded-width": `${config.expandedWidth}px`,
            ...(pos ? { left: `${pos.left}px`, top: `${pos.top}px` } : {}),
          }}
          role="dialog"
          aria-label={config.title}
        >
          {/* The header doubles as the drag handle (isDragHandle skips its own
              buttons) and, when collapsed, as the click target that restores the
              panel — the chat-window convention. */}
          <header
            class="kb-header"
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClick={(e) => {
              // The release that ends a drag still reaches here as a click; it
              // moved the bar, so it must not also restore it (SAT-1088).
              if (collapsed && !drag.current.moved && isDragHandle(e.target, 0)) {
                setCollapsed(false);
              }
            }}
          >
            {!collapsed && (
              <button
                class="kb-iconbtn"
                onClick={goBack}
                disabled={!canGoBack}
                aria-label="Back"
              >
                <BackIcon />
              </button>
            )}
            <span class="kb-title">{contact ? "Contact Support" : config.title}</span>
            <div class="kb-header-actions">
              {!collapsed && active && !contact && (
                <>
                  {copied && <span class="kb-copied">Copied</span>}
                  <button
                    class="kb-iconbtn"
                    onClick={() => copyShareLink(active.slug)}
                    aria-label="Copy link to article"
                    title="Copy link"
                  >
                    {copied ? <CheckIcon /> : <LinkIcon />}
                  </button>
                </>
              )}
              {!collapsed && (
                <button
                  class="kb-iconbtn"
                  // Dragging is a manual placement; going full-size discards it
                  // so the big panel lands where the CSS dock puts it.
                  onClick={() => {
                    setExpanded((v) => !v);
                    setPos(null);
                  }}
                  aria-label={expanded ? "Shrink panel" : "Expand panel"}
                  title={expanded ? "Shrink" : "Expand"}
                >
                  {expanded ? <CompressIcon /> : <ExpandIcon />}
                </button>
              )}
              <button
                class="kb-iconbtn"
                onClick={() => {
                  // Minimizing always docks the bar at the bottom corner, the
                  // chat-window convention, wherever the panel had been dragged
                  // (SAT-1088). The bar can still be dragged once collapsed.
                  if (!collapsed) setPos(null);
                  setCollapsed((v) => !v);
                }}
                aria-label={collapsed ? "Restore panel" : "Collapse panel"}
                aria-expanded={!collapsed}
                title={collapsed ? "Restore" : "Collapse"}
              >
                {collapsed ? <ChevronUpIcon /> : <MinusIcon />}
              </button>
              <button class="kb-iconbtn" onClick={closeAll} aria-label="Close" title="Close">
                <CloseIcon />
              </button>
            </div>
          </header>

          {/* Collapsed: the header bar is the whole panel. Nothing below it is
              rendered, but none of its state is discarded — restoring comes
              back to the same article, question and answer. */}
          {!collapsed && (contact ? (
            <SupportForm
              config={config}
              article={active}
              draft={supportDraft}
              onDone={() => setContact(false)}
            />
          ) : active ? (
            <div class="kb-body">
              <article class="kb-article">
                <h1>{active.title}</h1>
                <p class="kb-article-meta">
                  {[active.folder.split("/").join(" / "), `${readingMinutes(active.content)} min read`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {/* eslint-disable-next-line react/no-danger */}
                <div
                  class="kb-content"
                  ref={contentRef}
                  onClick={onContentClick}
                  dangerouslySetInnerHTML={{ __html: articleHtml }}
                />
              </article>
            </div>
          ) : (
            listBody
          ))}

          {/* Bottom chrome: rate the article, open it in the standalone app, and
              the direct line to Customer Support for when the KB doesn't answer
              it. The vote row lives here rather than after the prose so a short
              article doesn't strand it mid-panel — and so it stays reachable on
              a long one without scrolling to the very end.

              Hidden while collapsed, and while the contact form is up (the form
              has its own actions, and "Contact support" beneath it is noise). */}
          {!collapsed && !contact && (active || supportOk) && (
            <footer class="kb-footer">
              {active && (
                <div class="kb-vote">
                  {vote === null ? (
                    <>
                      <span class="kb-vote-q">Was this article helpful?</span>
                      <button class="kb-vote-btn" onClick={() => castVote(true)}>
                        <ThumbUpIcon />
                        Yes
                      </button>
                      <button class="kb-vote-btn" onClick={() => castVote(false)}>
                        <ThumbDownIcon />
                        No
                      </button>
                    </>
                  ) : (
                    <span class="kb-vote-q">
                      {vote ? "Thanks for the feedback!" : "Thanks — we'll work on this one."}
                    </span>
                  )}
                </div>
              )}
              {/* The same article outside the host product: the standalone
                  Knowledge Base app, where it can be bookmarked, printed,
                  shared with a colleague, or read at full width. */}
              {standaloneHref && (
                <a
                  class="kb-support kb-standalone"
                  href={standaloneHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLinkIcon />
                  Open in the Knowledge Base
                </a>
              )}
              {supportOk && (
                <button type="button" class="kb-support" onClick={() => setContact(true)}>
                  <MessageIcon />
                  Contact support
                </button>
              )}
            </footer>
          )}
        </section>
      )}

      {lightboxSrc && (
        <div
          class="kb-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setLightboxSrc(null)}
        >
          <button class="kb-lightbox-close" aria-label="Close preview" onClick={() => setLightboxSrc(null)}>
            <CloseIcon />
          </button>
          {/* stopPropagation so clicking the image itself doesn't dismiss */}
          <img src={lightboxSrc} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
